import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react"

interface PaginationProps {
  total: number
  limit: number
  offset: number
  onPageChange: (newOffset: number) => void
  onLimitChange: (newLimit: number) => void
  pageSizeOptions?: number[]
  showSummary?: boolean
  showPageSizeSelect?: boolean
  showPageCount?: boolean
  showFirstLast?: boolean
}

export const Pagination = ({
  total,
  limit,
  offset,
  onPageChange,
  onLimitChange,
  pageSizeOptions,
  showSummary = true,
  showPageSizeSelect = true,
  showPageCount = true,
  showFirstLast = true
}: PaginationProps) => {
  const currentPage = Math.floor(offset / limit) + 1
  const totalPages = Math.ceil(total / limit)
  const startItem = offset + 1
  const endItem = Math.min(offset + limit, total)

  const pageSizes = pageSizeOptions ?? [5, 10, 20, 30, 50, 100]

  const handlePrevious = () => {
    if (offset > 0) {
      onPageChange(Math.max(0, offset - limit))
    }
  }

  const handleNext = () => {
    if (offset + limit < total) {
      onPageChange(offset + limit)
    }
  }

  const handleFirstPage = () => {
    onPageChange(0)
  }

  const handleLastPage = () => {
    onPageChange((totalPages - 1) * limit)
  }

  if (total === 0) {
    return null
  }

  return (
    <div className="flex w-full items-center justify-between px-2 mt-4">
      <div className="flex items-center space-x-2">
        {showSummary && (
          <p className="text-sm font-medium text-primary">Showing {startItem}-{endItem} of {total} entries</p>
        )}
        {showPageSizeSelect && (
          <Select
            value={limit.toString()}
            onValueChange={(value) => {
              const newLimit = parseInt(value)
              onLimitChange(newLimit)
            }}
          >
            <SelectTrigger className="h-8 w-[70px] bg-foreground text-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top" className="bg-foreground text-primary">
              {pageSizes.map((pageSize) => (
                <SelectItem key={pageSize} value={pageSize.toString()}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex items-center space-x-2">
        {showFirstLast && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleFirstPage}
            disabled={offset === 0}
            className="h-8 w-8 p-0"
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrevious}
          disabled={offset === 0}
          className="h-8 w-8 p-0"
        >
          <span className="sr-only">Go to previous page</span>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {showPageCount && (
          <p className="text-sm font-medium text-primary px-2">
            Page {currentPage} of {totalPages}
          </p>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          disabled={offset + limit >= total}
          className="h-8 w-8 p-0"
        >
          <span className="sr-only">Go to next page</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
        {showFirstLast && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleLastPage}
            disabled={offset + limit >= total}
            className="h-8 w-8 p-0"
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}
